import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Field } from '@promtver/ui-kit';
import type { AnalyticsSummary } from '@promtver/contracts';

import { getAnalytics, getExportJournal, logExport, requestServerExport } from '../../api/admin';
import { errorMessage } from '../../api/errors';
import { AppShell } from '../../components/AppShell';
import { AsyncState } from '../../components/AsyncState';
import { KeyValue } from '../../components/KeyValue';
import { Select } from '../../components/Select';
import { ru } from '../../i18n/ru';
import { downloadCsv, type CsvCell } from '../../lib/csv';
import { addDaysInputValue, formatDateTimeLocal, formatPercent, todayInputValue } from '../../lib/datetime';
import { downloadXlsx, type XlsxSheet } from '../../lib/xlsx';
import { categoryLabel } from '../../lib/format';

type ReportType = 'visits' | 'revenue' | 'bookings' | 'districts' | 'categories';
type ExportFormat = 'csv' | 'xlsx';

const REPORT_TYPES: Array<{ value: ReportType; label: string }> = [
  { value: 'visits', label: ru.exports.reportVisits },
  { value: 'revenue', label: ru.exports.reportRevenue },
  { value: 'bookings', label: ru.exports.reportBookings },
  { value: 'districts', label: ru.exports.reportDistricts },
  { value: 'categories', label: ru.exports.reportCategories },
];

function reportTitle(type: ReportType): string {
  return REPORT_TYPES.find((item) => item.value === type)?.label ?? type;
}

/** Строки отчёта по аналитике. Персональные данные не выводятся (FR-OBS-03). */
function buildReportRows(type: ReportType, summary: AnalyticsSummary, from: string, to: string): CsvCell[][] {
  const rows: CsvCell[][] = [];
  rows.push([reportTitle(type)]);
  rows.push([ru.common.period, from, to]);
  rows.push([ru.common.date, new Date().toISOString()]);
  rows.push([]);

  if (type === 'bookings') {
    rows.push([ru.analytics.bookings, summary.bookings]);
    rows.push([ru.analytics.cancellations, summary.cancellations]);
    rows.push([ru.analytics.cancellationRate, formatPercent(summary.cancellationRate)]);
    rows.push([ru.analytics.repeatRate, formatPercent(summary.repeatRate)]);
    rows.push([]);
  }

  if (type === 'revenue') {
    rows.push([ru.analytics.averageCheck, (summary.averageCheck / 100).toFixed(2)]);
    rows.push([]);
  }

  if (type === 'visits' || type === 'districts') {
    rows.push([ru.analytics.byDistrict, ru.analytics.visits]);
    for (const item of summary.byDistrict) {
      rows.push([item.district, item.visits]);
    }
    rows.push([]);
  }

  if (type === 'visits' || type === 'categories') {
    rows.push([ru.analytics.byCategory, ru.analytics.visits]);
    for (const item of summary.byCategory) {
      rows.push([categoryLabel(item.category), item.visits]);
    }
    rows.push([]);
  }

  if (type === 'visits') {
    rows.push([ru.analytics.byAge, ru.analytics.visits]);
    for (const item of summary.byAge) {
      rows.push([item.bucket, item.visits]);
    }
    rows.push([]);
  }

  rows.push([ru.analytics.visits, summary.visits]);
  return rows;
}

/**
 * Выгрузка отчётов (FR-ADM-05): XLSX и CSV в UTF-8 с BOM.
 * Каждая выгрузка фиксируется в журнале с указанием пользователя и времени.
 */
export function ExportsPage() {
  const queryClient = useQueryClient();
  const [reportType, setReportType] = useState<ReportType>('visits');
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [from, setFrom] = useState(addDaysInputValue(-30));
  const [to, setTo] = useState(todayInputValue());
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serverUrl, setServerUrl] = useState<string | null>(null);

  const journalQuery = useQuery({
    queryKey: ['admin', 'export-journal'],
    queryFn: () => getExportJournal(),
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      const response = await getAnalytics({ from, to });
      const summary = response.summary;
      const rows = buildReportRows(reportType, summary, from, to);
      const filename = `promtver-${reportType}-${from}_${to}`;

      if (format === 'csv') {
        downloadCsv(`${filename}.csv`, rows);
      } else {
        const sheet: XlsxSheet = {
          name: reportTitle(reportType).slice(0, 31),
          rows,
        };
        downloadXlsx(`${filename}.xlsx`, [sheet]);
      }

      await logExport({
        format,
        reportType,
        from,
        to,
        rowCount: rows.length,
      });
      return rows.length;
    },
    onSuccess: async (rowCount) => {
      setError(null);
      setNotice(`${ru.exports.downloaded} (${rowCount})`);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'export-journal'] });
    },
    onError: (cause) => setError(errorMessage(cause, ru.common.unknownError)),
  });

  const serverExportMutation = useMutation({
    mutationFn: () => requestServerExport(format, from, to),
    onSuccess: (result) => {
      setError(null);
      setServerUrl(result.url);
    },
    onError: (cause) => setError(errorMessage(cause, ru.common.unknownError)),
  });

  const journal = journalQuery.data?.items ?? [];

  return (
    <AppShell title={ru.exports.title} subtitle={ru.exports.subtitle}>
      <Card title={ru.exports.reportType}>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            label={ru.exports.reportType}
            value={reportType}
            onChange={(value) => setReportType(value as ReportType)}
            options={REPORT_TYPES}
          />
          <Select
            label={ru.exports.format}
            value={format}
            onChange={(value) => setFormat(value as ExportFormat)}
            options={[
              { value: 'csv', label: ru.exports.formatCsv },
              { value: 'xlsx', label: ru.exports.formatXlsx },
            ]}
          />
          <Field label={ru.common.from} value={from} onChange={setFrom} type="date" required />
          <Field label={ru.common.to} value={to} onChange={setTo} type="date" required />
        </div>

        <Button
          tone="accent"
          disabled={exportMutation.isPending}
          onClick={() => exportMutation.mutate()}
        >
          {ru.exports.download}
        </Button>

        <div className="flex flex-col gap-2 border-t border-line pt-3">
          <span className="text-xs text-muted">{ru.exports.serverExportHint}</span>
          <div>
            <Button
              variant="outline"
              disabled={serverExportMutation.isPending}
              onClick={() => serverExportMutation.mutate()}
            >
              {ru.exports.serverExport}
            </Button>
          </div>
          {serverUrl ? (
            <a
              href={serverUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="text-sm text-accent underline"
            >
              {ru.exports.serverExportLink}
            </a>
          ) : null}
        </div>

        {notice ? <p className="text-xs text-success">{notice}</p> : null}
        {error ? (
          <p role="alert" className="text-xs text-danger">
            {error}
          </p>
        ) : null}
      </Card>

      <Card title={ru.exports.journal}>
        <AsyncState
          isLoading={journalQuery.isLoading}
          isError={journalQuery.isError}
          isEmpty={journal.length === 0}
          emptyTitle={ru.exports.journalEmpty}
          onRetry={() => void journalQuery.refetch()}
        >
          <ul className="flex flex-col gap-2">
            {journal.map((record) => (
              <li key={record.id} className="rounded-card border border-line p-3">
                <div className="flex flex-col gap-1">
                  <KeyValue label={ru.exports.journalUser} value={record.userName} />
                  <KeyValue label={ru.exports.journalFormat} value={record.format.toUpperCase()} mono />
                  <KeyValue label={ru.exports.reportType} value={record.reportType} />
                  <KeyValue label={ru.exports.journalRows} value={record.rowCount} mono />
                  <KeyValue label={ru.exports.journalTime} value={formatDateTimeLocal(record.exportedAt)} />
                </div>
              </li>
            ))}
          </ul>
        </AsyncState>
      </Card>
    </AppShell>
  );
}
